import {
    User, UserManager, UserManagerSettings, WebStorageStateStore,
} from 'oidc-client';

type AuthenticationLogger = (error?: Error | unknown) => void

// Floor on how often `getValidAccessToken` may trigger a renewal. It is called from a 5s polling
// loop, so without this a dead token would mean a renewal request every 5 seconds.
const RENEW_COOLDOWN_MS = 30000;

export class AuthenticationService {
    private userManager: UserManager | null;

    private renewPromise: Promise<User | null> | null = null;

    private lastRenewAt = 0;

    public logger: AuthenticationLogger = () => null;

    constructor(config?: null | UserManagerSettings, logger?: AuthenticationLogger) {
        if (config) {
            this.init(config, logger);
        }

        this.userManager = null;
    }

    private userManagerUninitialized = () => {
        this.logger(new Error('userManager is not initialized'));
    };

    init = (config: UserManagerSettings, logger?: AuthenticationLogger) => {
        this.userManager = new UserManager(config);
        if (typeof logger === 'function') {
            this.logger = logger;
        }

        return this;
    };

    getUserManager = () => {
        if (!this.userManager) {
            throw new Error('userManager is not initialized');
        }

        return this.userManager;
    };

    getUser = (): Promise<User | null> => {
        if (!this.userManager) {
            this.userManagerUninitialized();

            return Promise.resolve(null);
        }

        return this.userManager.getUser();
    };

    logout = async () => {
        if (!this.userManager) {
            this.userManagerUninitialized();

            return;
        }
        const user = await this.getUser();

        // @ts-ignore hot fix please refer to https://github.com/IdentityModel/oidc-client-js/issues/570#issuecomment-433557489
        await this.userManager.clearStaleState(new WebStorageStateStore({ store: window.localStorage }));

        if (user) {
            await this.userManager.signoutRedirect();
        } else {
            await this.userManager.signinRedirect();
        }
    };

    login = async () => {
        if (!this.userManager) {
            this.userManagerUninitialized();

            return;
        }

        await this.userManager.signinRedirect();
    };

    signoutRedirect = async () => {
        if (!this.userManager) {
            this.userManagerUninitialized();

            return;
        }

        await this.userManager.signoutRedirect();
    };

    signinSilent = async (): Promise<User | null> => {
        if (!this.userManager) {
            this.userManagerUninitialized();

            return null;
        }

        // Collapse concurrent renewals: the interval token check, the expiring event and a 401
        // handler can all ask for a renewal within the same second.
        if (!this.renewPromise) {
            this.renewPromise = this.userManager.signinSilent()
                .finally(() => { this.renewPromise = null; });
        }

        return this.renewPromise;
    };

    /**
     * Returns the signed-in user's access token, for callers that need a token good enough to send
     * with an API call. Returns null when there is no OIDC session (anonymous flows) or when the
     * session is dead.
     *
     * Renewing on a schedule is `automaticSilentRenew`'s job, not this method's — it is called from
     * a 5s polling loop, so it renews only when the token is genuinely unusable, and at most once
     * per cooldown.
     */
    getValidAccessToken = async (): Promise<string | null> => {
        if (!this.userManager) {
            this.userManagerUninitialized();

            return null;
        }

        const user = await this.getUser();

        if (!user) { return null; }

        // Still usable. Note this deliberately ignores the 30s "expiring soon" buffer that
        // `Security.isTokenExpired` applies: a token with 10s left is a far better thing to send
        // than a client_credentials token with no member subject.
        if (!user.expired) { return user.access_token; }

        const now = Date.now();

        if (now - this.lastRenewAt < RENEW_COOLDOWN_MS) { return null; }

        this.lastRenewAt = now;

        try {
            const renewed = await this.signinSilent();

            if (renewed?.access_token && !renewed.expired) { return renewed.access_token; }
        } catch (error) {
            this.logger(error);
        }

        return null;
    };
}
